import React from 'react';
import { CP_PER_GOLD, CP_PER_SILVER, formatCoinFull } from '../utils/coin';

/**
 * Visual Coin amounts — denomination icons (gold/silver/copper, each in its
 * metal's color) instead of text like "3 silver · 50 copper".
 *
 * <CoinAmount cp={350} />            → [silver] 3 [copper] 50
 * <CoinAmount cp={350} compact />    → [silver] 3.5   (dominant denomination)
 * Sizing follows the surrounding font (icons are em-based).
 */

export type CoinDenom = 'gold' | 'silver' | 'copper';

/** Exported so the reward medallion draws the SAME coin, at its own size. */
export const DENOM_COLORS: Record<CoinDenom, { fill: string; rim: string; shine: string }> = {
  gold: { fill: '#FACC15', rim: '#A16207', shine: '#FEF08A' },
  silver: { fill: '#D1D5DB', rim: '#6B7280', shine: '#F3F4F6' },
  copper: { fill: '#C77B3B', rim: '#8B5A2B', shine: '#E8A76B' },
};

export function CoinIcon({ denom, className = '' }: { denom: CoinDenom; className?: string }) {
  const c = DENOM_COLORS[denom];
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className={`inline-block w-[1.05em] h-[1.05em] shrink-0 ${className}`}
    >
      <circle cx="10" cy="10" r="9" fill={c.fill} stroke={c.rim} strokeWidth="1.6" />
      <circle cx="10" cy="10" r="5.6" fill="none" stroke={c.rim} strokeWidth="1" opacity="0.55" />
      <path d="M 5.2 6.4 A 6 6 0 0 1 9 4.2" fill="none" stroke={c.shine} strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/**
 * One decimal, TRUNCATED — never rounded.
 *
 * ⚠️⚠️ `toFixed(1)` ROUNDS, and on a balance it rounds the wrong way: 29,720cp
 * (2 gold 97 silver 20 copper) came out as "3", claiming a whole gold the user
 * did not have. Showing more coin than someone holds is the dangerous
 * direction — it invites them to start something they cannot pay for.
 * Truncating gives "2.9", which is short of the truth and never over it.
 * ⭐ Authored prices are unaffected: 12,000cp is exactly 1.2 either way.
 */
function trim(n: number): string {
  return (Math.floor(n * 10) / 10).toFixed(1).replace(/\.0$/, '');
}

export function CoinAmount({
  cp,
  compact = false,
  className = '',
}: {
  cp: number;
  compact?: boolean;
  className?: string;
}) {
  const abs = Math.abs(cp);
  const neg = cp < 0;

  const parts: { denom: CoinDenom; text: string }[] = [];
  if (compact) {
    if (abs >= CP_PER_GOLD) parts.push({ denom: 'gold', text: trim(abs / CP_PER_GOLD) });
    else if (abs >= CP_PER_SILVER) parts.push({ denom: 'silver', text: trim(abs / CP_PER_SILVER) });
    else parts.push({ denom: 'copper', text: String(abs) });
  } else {
    // Full breakdown always shows all three denominations — a 0 next to the
    // gold coin reads as an aspiration, not an absence.
    const gold = Math.floor(abs / CP_PER_GOLD);
    const silver = Math.floor((abs % CP_PER_GOLD) / CP_PER_SILVER);
    const copper = abs % CP_PER_SILVER;
    parts.push(
      { denom: 'gold', text: String(gold) },
      { denom: 'silver', text: String(silver) },
      { denom: 'copper', text: String(copper) },
    );
  }

  return (
    <span
      role="img"
      aria-label={formatCoinFull(cp)}
      className={`inline-flex items-center gap-x-1.5 align-middle tabular-nums ${className}`}
    >
      {neg && <span aria-hidden="true">−</span>}
      {parts.map((p) => (
        <span key={p.denom} className="inline-flex items-center gap-x-0.5">
          <CoinIcon denom={p.denom} />
          <span aria-hidden="true">{p.text}</span>
        </span>
      ))}
    </span>
  );
}
